import { useState } from 'react';
import {
  Gift,
  Copy,
  Check,
  Users,
  DollarSign,
  Share2,
  Mail,
  Twitter,
  Linkedin,
  MessageSquare,
  ArrowRight,
  ArrowUpRight,
  Shield,
  Globe,
  Repeat,
  Zap,
  Clock,
  ChevronDown,
  TrendingUp,
  Award,
} from 'lucide-react';
import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';

const referralLink = 'https://portal.zurihub.io/signup?ref=your-code';

const steps = [
  { icon: 'Share2', title: 'Share your link', text: 'Send your unique referral link to friends, partners, and customers who need better customer experience tools.' },
  { icon: 'Users', title: 'They sign up', text: 'They create a workspace, start a free trial, and experience the unified inbox, AI support, and omnichannel messaging.' },
  { icon: 'Gift', title: 'Both get $100 credit', text: 'You and your referral each earn $100 in account credit when they subscribe to a paid plan.' },
  { icon: 'DollarSign', title: 'You earn $50 commission', text: 'After 90 days of their active paid subscription, you receive a $50 commission—recurring value for every successful referral.' },
];

const whyRefer = [
  { icon: 'DollarSign', title: 'Double reward structure', text: 'You earn $50 commission and your friend gets $100 credit when they subscribe—a win-win for everyone.' },
  { icon: 'Globe', title: 'Unlimited earning potential', text: 'Refer agencies, founders, support teams, or anyone who needs better CX. No caps on referrals or earnings.' },
  { icon: 'Repeat', title: 'Recurring commissions', text: 'Commissions are paid after 90 days of active paid subscription, ensuring quality referrals that stick around.' },
  { icon: 'Shield', title: 'Trusted by 250+ teams', text: 'ZuriHub is used by teams in e-commerce, SaaS, healthcare, finance, education, and travel—easy to recommend with confidence.' },
];

const whoCanRefer = [
  { title: 'Existing ZuriHub customers', text: 'Anyone with an active ZuriHub workspace automatically gets a personal referral code to share.' },
  { title: 'Agencies and consultants', text: 'Recommend ZuriHub to clients and earn commission on each successful signup while providing them with better CX tools.' },
  { title: 'Influencers and content creators', text: 'Share ZuriHub with your audience through content, reviews, or recommendations and get paid for every subscription.' },
  { title: 'Technology and implementation partners', text: 'Partners can refer and co-sell ZuriHub as part of their service offerings, creating additional revenue streams.' },
];

const successStories = [
  { stat: '$2,500+', label: 'Earned by top referrers in 90 days' },
  { stat: '150+', label: 'Successful referrals per month' },
  { stat: '85%', label: 'Referral satisfaction rate' },
  { stat: '4.8/5', label: 'Average product rating' },
];

const rules = [
  'Referrals must sign up using your unique referral link to ensure proper tracking and attribution.',
  'The $100 credit applies after the referred workspace subscribes to a paid plan (Free, Pro, or Enterprise).',
  'The $50 commission is paid after the referred account maintains an active paid subscription for 90 consecutive days.',
  'Referrals must be new ZuriHub customers who have never owned a workspace before (no existing workspace owners).',
  'Self-referrals, fake accounts, coupon stacking, and referral fraud are strictly prohibited and will result in account suspension.',
  'Commissions are paid via the payment method on file after the 90-day qualification period is complete.',
];

const faqs = [
  { q: 'When do I receive my $50 commission?', a: 'You receive the $50 commission after your referral has maintained a paid ZuriHub subscription for 90 consecutive days. This ensures the referral is genuine and committed to the platform.' },
  { q: 'Is there a limit to how many people I can refer?', a: 'No. You can refer as many people as you like and earn unlimited commissions. There are no caps on referrals or earnings—your income potential is unlimited.' },
  { q: 'What if my referral signs up without my link?', a: 'The referral must sign up through your unique link for credit to be tracked and attributed to your account. Without the link, we cannot verify the referral relationship.' },
  { q: 'Can I refer my own company or team?', a: 'No. Self-referrals are not eligible for credits or commissions. The program is designed for genuine referrals to new customers, not for personal account upgrades.' },
  { q: 'How are commissions paid?', a: 'Commissions are paid via the payment method on file in your ZuriHub account after the 90-day qualification period. You can choose to receive payments via bank transfer, mobile money, or other supported methods.' },
  { q: 'What happens if my referral cancels before 90 days?', a: 'If your referral cancels their subscription before the 90-day qualification period, no commission is earned. The 90-day requirement ensures we reward genuine, committed customer relationships.' },
];

const shareLinks = [
  { icon: 'MessageSquare', label: 'WhatsApp', url: `https://wa.me/?text=${encodeURIComponent(`Check out ZuriHub - the AI-first customer experience platform that unifies chat, tickets, and omnichannel messaging. Sign up with my link and we both get $100 credit! ${referralLink}`)}` },
  { icon: 'Twitter', label: 'Twitter', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent('Just discovered ZuriHub - the AI-first customer experience platform. Unifies chat, tickets, WhatsApp, Instagram, and more in one workspace. Both get $100 credit when you sign up!')}&url=${encodeURIComponent(referralLink)}` },
  { icon: 'Linkedin', label: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}` },
  { icon: 'Mail', label: 'Email', url: `mailto:?subject=${encodeURIComponent('You should try ZuriHub')}&body=${encodeURIComponent(`I've been using ZuriHub for customer support and it's been a game-changer. It unifies chat, tickets, WhatsApp, Instagram, and more in one workspace with AI assistance.\n\nSign up with my link and we both get $100 credit:\n\n${referralLink}\n\nIt's perfect for teams that want to improve response times and customer satisfaction.`)}` },
];

function ReferralFaq({ question, answer, isOpen, onClick }: { question: string; answer: string; isOpen: boolean; onClick: () => void }) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 active:scale-[0.99] ${
        isOpen ? 'shadow-md' : ''
      }`}
    >
      <button onClick={onClick} className="flex w-full items-center justify-between text-left" aria-expanded={isOpen}>
        <span className="pr-4 font-semibold text-slate-900">{question}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr] pt-3' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <p className="text-slate-600">{answer}</p>
        </div>
      </div>
    </div>
  );
}

export default function Referral() {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Offer',
      name: 'ZuriHub Refer & Earn Program',
      description: 'Refer ZuriHub to friends, partners, and customers. Both get $100 credit when they subscribe. You earn $50 commission after 90 days of active subscription.',
      url: `${site.baseUrl}/referral`,
      price: '100',
      priceCurrency: 'USD',
      areaServed: 'Global',
      eligibleRegion: 'Global',
      availability: 'https://schema.org/InStock',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to refer ZuriHub and earn commissions',
      step: steps.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.title,
        text: s.text,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: site.name,
      url: site.baseUrl,
      description: site.description,
    },
  ];

  return (
    <>
      <SEO
        title="Refer & Earn - $100 Credit + $50 Commission per Referral"
        description="Join the ZuriHub referral program. Refer friends, partners, and customers to earn $100 credit + $50 commission per successful referral. Unlimited earning potential with a trusted CX platform."
        path="/referral"
        jsonLd={jsonLd}
      >
        <meta
          name="keywords"
          content="ZuriHub referral program, earn money referring CX software, customer support affiliate program, $100 credit referral bonus, $50 commission per referral, customer experience referral rewards"
        />
      </SEO>

      <section className="relative overflow-hidden bg-slate-950 px-4 py-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-900/20 px-4 py-1.5 text-sm font-medium text-brand-300">
            <Award className="h-4 w-4" /> Partner & earn
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Earn <span className="gradient-text">$100 credit + $50</span> per referral
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-300">
            Share ZuriHub with friends, partners, and customers. Both get $100 credit when they subscribe, and you earn $50 commission after 90 days. Unlimited earning potential with a platform trusted by 250+ teams.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-base font-semibold text-white transition hover:bg-brand-700"
            >
              Start free trial <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-7 py-3.5 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Learn more <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-10" aria-labelledby="trust-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { icon: 'Zap', title: 'Instant credit', text: '$100 credit applied immediately on subscription.' },
              { icon: 'Clock', title: '90-day commission', text: '$50 paid after active subscription period.' },
              { icon: 'Shield', title: 'Trusted platform', text: 'Used by 250+ teams across industries.' },
            ].map((t) => (
              <div key={t.title} className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-5">
                {t.icon === 'Zap' ? <Zap className="h-6 w-6 text-brand-600" /> : t.icon === 'Clock' ? <Clock className="h-6 w-6 text-brand-600" /> : <Shield className="h-6 w-6 text-brand-600" />}
                <div>
                  <p className="font-bold text-slate-900">{t.title}</p>
                  <p className="text-sm text-slate-600">{t.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-20" aria-labelledby="steps-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="steps-heading" className="section-title">How to earn with ZuriHub referrals</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Four simple steps to start earning commissions and credits with the ZuriHub referral program.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => {
              const StepIcon = step.icon === 'Share2' ? Share2 : step.icon === 'Users' ? Users : step.icon === 'Gift' ? Gift : DollarSign;
              return (
                <div
                  key={step.title}
                  className="rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700">
                    <StepIcon className="h-7 w-7" />
                  </div>
                  <span className="text-xs font-bold text-brand-600">Step {index + 1}</span>
                  <h3 className="mt-2 text-lg font-extrabold text-slate-900">{step.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{step.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" aria-labelledby="success-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="success-heading" className="section-title">Referral program success stories</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              See what top referrers are earning with the ZuriHub referral program.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {successStories.map((stat) => (
              <div
                key={stat.label}
                className="rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-sm"
              >
                <p className="text-4xl font-extrabold text-brand-700">{stat.stat}</p>
                <p className="mt-2 text-sm font-semibold text-slate-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" aria-labelledby="why-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="why-heading" className="section-title">Why refer ZuriHub</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              ZuriHub is the customer experience platform modern teams switch to. Here is why it is easy to recommend with confidence.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {whyRefer.map((w) => {
              const WhyIcon = w.icon === 'DollarSign' ? DollarSign : w.icon === 'Globe' ? Globe : w.icon === 'Repeat' ? Repeat : Shield;
              return (
                <div
                  key={w.title}
                  className="rounded-3xl border border-slate-100 bg-slate-50 p-8 transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner">
                    <WhyIcon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-extrabold text-slate-900">{w.title}</h3>
                  <p className="mt-3 text-slate-600">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" aria-labelledby="who-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="who-heading" className="section-title">Who can participate</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              The ZuriHub referral program is open to anyone who believes in better customer experience tools.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {whoCanRefer.map((w) => (
              <div
                key={w.title}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-extrabold text-slate-900">{w.title}</h3>
                <p className="mt-3 text-slate-600">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" aria-labelledby="link-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="link-heading" className="section-title">Get your personal referral link</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Create a free ZuriHub workspace to unlock your personal referral code and start earning.
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 p-8 text-white shadow-2xl sm:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
                <Gift className="h-6 w-6" />
              </div>
              <h3 className="text-2xl font-bold">Your sample referral link</h3>
            </div>
            <p className="mb-6 text-brand-100">
              This is how your personal referral link will look. Create a workspace to get your unique code.
            </p>
            <div className="mb-6 flex items-center gap-2 rounded-xl bg-white/10 p-2 backdrop-blur">
              <div className="flex-1 truncate px-4 py-2 font-mono text-sm">{referralLink}</div>
              <button
                onClick={copyToClipboard}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-50 transition"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="mb-8">
              <p className="text-sm font-semibold text-brand-200 mb-3">Share via:</p>
              <div className="flex items-center gap-3">
                {shareLinks.map((s) => {
                  const ShareIcon = s.icon === 'MessageSquare' ? MessageSquare : s.icon === 'Twitter' ? Twitter : s.icon === 'Linkedin' ? Linkedin : Mail;
                  return (
                    <a
                      key={s.label}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20 transition hover:bg-white/30"
                      aria-label={`Share on ${s.label}`}
                    >
                      <ShareIcon className="h-5 w-5" />
                    </a>
                  );
                })}
              </div>
            </div>
            <div className="pt-6 border-t border-white/20">
              <a
                href={site.workspaceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-brand-700 transition hover:bg-slate-100"
              >
                Start free trial <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" aria-labelledby="rules-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="rules-heading" className="section-title">Referral program rules</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Simple, transparent rules to ensure fair compensation for genuine referrals.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {rules.map((rule, i) => (
              <div
                key={i}
                className="flex items-start gap-4 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                  <Check className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-base font-semibold text-slate-900 leading-relaxed">{rule}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Frequently asked questions</h2>
          <p className="section-subtitle mx-auto mt-4">
            Everything you need to know about the ZuriHub referral program.
          </p>
          <div className="mt-10 space-y-4">
            {faqs.map((f, i) => (
              <ReferralFaq
                key={f.q}
                question={f.q}
                answer={f.a}
                isOpen={open === i}
                onClick={() => setOpen(open === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <div className="mb-4 flex items-center justify-center gap-2">
            <TrendingUp className="h-6 w-6" />
            <span className="text-sm font-semibold text-brand-200">Unlimited earning potential</span>
          </div>
          <h2 className="text-3xl font-extrabold sm:text-4xl">Start earning commissions today</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Join 250+ teams already using ZuriHub. Get your referral link, share with your network, and earn $100 credit + $50 commission per successful referral.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-brand-700 transition hover:bg-slate-100"
          >
            Start free trial <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
